import { useMemo } from "react";
import type { ReactNode } from "react";
import type { RoomView } from "@grundbesitz/shared";
import { AppHeader } from "../components/AppHeader";
import { DiceChart } from "../components/result/DiceChart";
import { RankingTable } from "../components/result/RankingTable";
import { StatsTable } from "../components/result/StatsTable";
import { WealthChart } from "../components/result/WealthChart";
import { WinnerHero } from "../components/result/WinnerHero";
import { Button } from "../components/ui/Button";
import { buildWealthSeries, rankPlayers, reasonText } from "../components/result/resultLogic";

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="mt-12">
      <h2 id={id} className="mb-4 font-display text-2xl font-semibold text-ink">
        {title}
      </h2>
      {children}
    </section>
  );
}

export function Result({ room, onLeave }: { room: RoomView; onLeave: () => void }) {
  const game = room.game;
  const ranking = useMemo(() => (game ? rankPlayers(game) : []), [game]);
  const series = useMemo(() => {
    if (!game) return [];
    const order = new Map(ranking.map((r, i) => [r.player.id, i]));
    return buildWealthSeries(game).sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
  }, [game, ranking]);

  if (!game) {
    return (
      <div className="flex min-h-dvh flex-col">
        <AppHeader />
        <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-10 sm:px-6">
          <h1 className="font-display text-3xl font-bold text-ink">Das Spiel ist beendet</h1>
          <p className="mt-2 text-ink-2">Für dieses Spiel liegen keine Ergebnisse vor.</p>
          <Button variant="primary" className="mt-6" onClick={onLeave}>
            Zur Startseite
          </Button>
        </main>
      </div>
    );
  }

  const winner = ranking.find((r) => r.isWinner) ?? null;

  return (
    <div className="flex min-h-dvh flex-col">
      <AppHeader />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 pb-16 sm:px-6 sm:py-10">
        <WinnerHero
          winner={winner?.player ?? null}
          isYou={winner !== null && winner.player.id === room.you.playerId}
          reason={reasonText(game.gameOverReason)}
          worth={winner?.worth ?? 0}
          turns={game.turnCounter}
          onLeave={onLeave}
        />

        <Section id="ranking-title" title="Rangliste">
          <RankingTable ranking={ranking} meId={room.you.playerId} />
        </Section>

        <Section id="stats-title" title="Statistik">
          <StatsTable ranking={ranking} stats={game.stats} />
          <div className="mt-10">
            <WealthChart series={series} />
          </div>
          <div className="mt-10 max-w-3xl">
            <DiceChart diceSums={game.stats.diceSums} />
          </div>
        </Section>
      </main>
    </div>
  );
}
